import React, { useContext, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { DataContext } from "App";
import { createSubscription, uploadPrescription } from "services/subscriptions";
import AuthService from "services/auth";
import { formatPrice } from "utils/formatPrice";
import { t } from "i18n";
import { getPrimaryProductImageSrc } from "utils/productImages";
import { FaCloudUploadAlt, FaCreditCard, FaInfoCircle, FaLock, FaMobileAlt, FaMotorcycle, FaRegCopy } from "react-icons/fa";
import useHouseWallet from 'hooks/useHouseWallet';

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function SubscriptionModal({ open, onClose, mode = "grocery", presetProduct = null, onCreated, subscription = null, data = null }) {
  const houseWallet = useHouseWallet();
  const { products, toast } = useContext(DataContext);

  const [frequency, setFrequency] = useState("weekly");
  const [dayOfWeek, setDayOfWeek] = useState(1);
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [startDate, setStartDate] = useState(todayISO());

  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [addressLine, setAddressLine] = useState("");
  const [city, setCity] = useState("");
  // Payment UI state (required by spec)
  const [selectedPayment, setSelectedPayment] = useState("COD");
  const [cardNumber, setCardNumber] = useState("");
  const [cardExpiry, setCardExpiry] = useState("");
  const [cardCvv, setCardCvv] = useState("");

  const [items, setItems] = useState(() => {
    if (presetProduct) return [{ product_id: presetProduct.id, quantity: 1 }];
    return [];
  });

  const [prescriptionFile, setPrescriptionFile] = useState(null);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const productMap = useMemo(() => {
    const map = new Map();
    (products || []).forEach((p) => map.set(p.id, p));
    return map;
  }, [products]);

  const dayNames = useMemo(() => ["mon", "tue", "wed", "thu", "fri", "sat", "sun"].map((d) => t(`modules.options.${d}`)), []);

  const ordinal = (n) => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return t("subs.ordinal", { n, suffix: s[(v - 20) % 10] || s[v] || s[0] });
  };

  const scheduleLabel = useMemo(() => {
    if (frequency === "daily") return t("subs.daily");
    if (frequency === "weekly") return t("subs.everyDay", { day: dayNames[dayOfWeek] || t("subs.day") });
    return t("subs.everyMonthDay", { day: ordinal(dayOfMonth) });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frequency, dayOfWeek, dayOfMonth, dayNames]);

  const totalAmount = useMemo(() => {
    const sum = (items || []).reduce((acc, it) => {
      const p = productMap.get(it.product_id);
      const price = Number(p?.price || 0);
      const qty = Number(it.quantity || 0);
      return acc + price * qty;
    }, 0);
    return Math.max(0, sum);
  }, [items, productMap]);

  const paymentMethodForApi = useMemo(() => {
    if (selectedPayment === "ONLINE") return "card";
    if (selectedPayment === "JAZZCASH") return "jazzcash";
    return "cod";
  }, [selectedPayment]);

  const submitLabel = useMemo(() => {
    if (selectedPayment === "ONLINE") return t("subs.payNowAmount", { amount: `Rs. ${Math.round(totalAmount).toLocaleString()}` });
    if (selectedPayment === "JAZZCASH") return t("checkout.submit.jazz");
    return t("checkout.submit.place");
  }, [selectedPayment, totalAmount]);

  const subscriptionStatus = subscription?.status || subscription?.review_status || data?.status || data?.review_status || "pending_review";
  const showPaymentMethod = subscriptionStatus === "quoted" || subscriptionStatus === "quote_received" || subscriptionStatus === "awaiting_user_confirmation";

  const addItem = (productId) => {
    if (!productId) return;
    const pid = Number(productId);
    setItems((prev) => {
      const existing = prev.find((x) => x.product_id === pid);
      if (existing) return prev.map((x) => (x.product_id === pid ? { ...x, quantity: x.quantity + 1 } : x));
      return [...prev, { product_id: pid, quantity: 1 }];
    });
  };

  const updateQty = (pid, qty) => {
    setItems((prev) =>
      prev
        .map((x) => (x.product_id === pid ? { ...x, quantity: Math.max(1, Number(qty || 1)) } : x))
        .filter((x) => x.quantity > 0)
    );
  };

  const removeItem = (pid) => setItems((prev) => prev.filter((x) => x.product_id !== pid));

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  const submit = async () => {
    if (!AuthService.isAuthenticated()) {
      toast?.error?.(t("subs.errors.login"));
      return;
    }

    if (!fullName.trim()) {
      toast?.error?.(t("subs.errors.name"));
      return;
    }
    if (!phone.trim()) {
      toast?.error?.(t("subs.errors.phone"));
      return;
    }
    if (!addressLine.trim()) {
      toast?.error?.(t("subs.errors.address"));
      return;
    }
    if (!city.trim()) {
      toast?.error?.(t("subs.errors.city"));
      return;
    }

    if (!message.trim()) {
      toast?.error?.(t("subs.errors.message"));
      return;
    }

    setSubmitting(true);
    try {
      let prescription_file = null;
      // For JazzCash, proof screenshot is required.
      if (selectedPayment === "JAZZCASH" && !prescriptionFile) {
        toast?.error?.(t("subs.errors.screenshot"));
        return;
      }

      if (prescriptionFile) {
        const up = await uploadPrescription(prescriptionFile);
        if (!up.success) {
          toast?.error?.(up.error || t("subs.errors.upload"));
          return;
        }
        prescription_file = up.url;
      }

      const payload = {
        frequency,
        day_of_week: frequency === "weekly" ? dayOfWeek : null,
        day_of_month: frequency === "monthly" ? dayOfMonth : null,
        start_date: startDate,
        status: "active",
        prescription_file,
        message: message.trim(),
        full_name: fullName.trim(),
        phone: phone.trim(),
        address_line: addressLine.trim(),
        city: city.trim(),
        payment_method: paymentMethodForApi,
        items,
      };

      const res = await createSubscription(payload);
      if (!res.success) {
        toast?.error?.(res.error || t("subs.errors.create"));
        return;
      }

      toast?.success?.(`${t("subs.created")} ✅`);
      onCreated?.(res.data);
      onClose?.();
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <button className="absolute inset-0 z-50 bg-transparent" onClick={onClose} aria-label={t("subs.closeModal")} />

      <div className="relative z-[60] w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-[2rem] border border-slate-200 bg-white shadow-[0_30px_90px_rgba(15,23,42,0.25)]">
        <div className="p-6 sm:p-8">
          <div className="flex items-start justify-between gap-6">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-amber-700 bg-amber-500/10 inline-flex px-3 py-1 rounded-full">
                {t("nav.subscription")}
              </p>
              <h3 className="mt-4 text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">
                {mode === "grocery"
                  ? t("subs.modes.grocery")
                  : mode === "medicine"
                    ? t("subs.modes.medicine")
                    : mode === "flower"
                      ? t("subs.modes.flower")
                      : t("subs.modes.custom")}
              </h3>
              <p className="mt-2 text-sm text-slate-500">
                {t("subs.intro")}
              </p>
            </div>
            <button
              onClick={onClose}
              className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              {t("common.close")}
            </button>
          </div>

          <div className="mt-6 flex flex-col w-full gap-y-8">
            <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
              <p className="text-sm font-semibold text-slate-900">{t("subs.deliverySchedule")}</p>
              <div className="mt-4 grid gap-4">
                <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-600">
                  {t("subs.fullName")}
                  <input
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm"
                    placeholder={t("subs.namePlaceholder")}
                  />
                </label>

                <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-600">
                  {t("checkout.fields.phone")}
                  <input
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm"
                    placeholder={t("subs.phonePlaceholder")}
                  />
                </label>

                <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-600">
                  {t("subs.addressLine")}
                  <input
                    value={addressLine}
                    onChange={(e) => setAddressLine(e.target.value)}
                    className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm"
                    placeholder={t("subs.addressPlaceholder")}
                  />
                </label>

                <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-600">
                  {t("checkout.fields.city")}
                  <input
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm"
                    placeholder={t("checkout.placeholders.shipping_city")}
                  />
                </label>

                <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-600">
                  {t("subs.frequency")}
                  <select
                    value={frequency}
                    onChange={(e) => setFrequency(e.target.value)}
                    className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm"
                  >
                    <option value="daily">{t("subs.freq.daily")}</option>
                    <option value="weekly">{t("subs.freq.weekly")}</option>
                    <option value="monthly">{t("subs.freq.monthly")}</option>
                  </select>
                </label>

                <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
                  <span className="font-semibold">{scheduleLabel}</span>
                  {frequency === "daily" ? (
                    <span className="block mt-1 text-xs text-slate-500">{t("subs.dailyNote")}</span>
                  ) : null}
                </div>

                {frequency === "daily" ? null : frequency === "weekly" ? (
                  <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-600">
                    {t("subs.dayOfWeek")}
                    <select
                      value={dayOfWeek}
                      onChange={(e) => setDayOfWeek(Number(e.target.value))}
                      className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm"
                    >
                      {dayNames.map((name, i) => (
                        <option key={i} value={i}>{name}</option>
                      ))}
                    </select>
                  </label>
                ) : (
                  <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-600">
                    {t("subs.dayOfMonth")}
                    <select
                      value={dayOfMonth}
                      onChange={(e) => setDayOfMonth(Number(e.target.value))}
                      className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm"
                    >
                      {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                        <option key={d} value={d}>
                          {ordinal(d)}
                        </option>
                      ))}
                    </select>
                  </label>
                )}

                <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-600">
                  {t("subs.startDate")}
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm"
                  />
                </label>

                {selectedPayment !== "JAZZCASH" ? (
                  <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-600">
                    {t("subs.attachment")}
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => setPrescriptionFile(e.target.files?.[0] || null)}
                      className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm"
                    />
                  </label>
                ) : null}

                <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-600">
                  {t("subs.message")}
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={4}
                    placeholder={
                      mode === "medicine"
                        ? t("subs.placeholders.medicine")
                        : mode === "custom"
                          ? t("subs.placeholders.custom")
                        : t("subs.placeholders.grocery")
                    }
                    className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm"
                  />
                </label>
              </div>
            </div>

            {/* MIDDLE: Payment Method (only after quote is received) */}
            {showPaymentMethod ? (
              <div className="w-full rounded-3xl border border-slate-200 bg-slate-50 p-5">
                <p className="text-sm font-semibold text-slate-900">{t("checkout.paymentMethod")}</p>
                <div className="mt-4 relative w-full mb-6 gap-y-6 flex flex-col">
                  <div className="w-full flex flex-row justify-between gap-4 p-1 rounded-xl mb-4 border border-gray-200 bg-white z-10">
                    <button
                      type="button"
                      onClick={() => setSelectedPayment("ONLINE")}
                      className={`flex-1 rounded-xl p-4 text-start transition ${
                        selectedPayment === "ONLINE"
                          ? "bg-orange-100 border-2 border-orange-400"
                          : "border border-gray-200 bg-white hover:bg-slate-50"
                      }`}
                    >
                      <div className="flex items-start gap-3 min-w-0">
                        <div className="rounded-lg bg-orange-100 p-2 text-orange-700">
                          <FaCreditCard />
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-extrabold text-slate-900">{t("checkout.tiles.online")}</div>
                          <div className="text-sm text-gray-500">{t("checkout.tiles.onlineSub")}</div>
                        </div>
                      </div>
                    </button>

                    {houseWallet.jazzcash_number && (
                    <button
                      type="button"
                      onClick={() => setSelectedPayment("JAZZCASH")}
                      className={`flex-1 rounded-xl p-4 text-start transition ${
                        selectedPayment === "JAZZCASH"
                          ? "bg-orange-100 border-2 border-orange-400"
                          : "border border-gray-200 bg-white hover:bg-slate-50"
                      }`}
                    >
                      <div className="flex items-start gap-3 min-w-0">
                        <div className="rounded-lg bg-orange-100 p-2 text-orange-700">
                          <FaMobileAlt />
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-extrabold text-slate-900">{t("paymentMethod.jazzcash")}</div>
                          <div className="text-sm text-gray-500">{t("checkout.tiles.jazzSub")}</div>
                        </div>
                      </div>
                    </button>
                    )}

                    <button
                      type="button"
                      onClick={() => setSelectedPayment("COD")}
                      className={`flex-1 rounded-xl p-4 text-start transition ${
                        selectedPayment === "COD"
                          ? "bg-orange-100 border-2 border-orange-400"
                          : "border border-gray-200 bg-white hover:bg-slate-50"
                      }`}
                    >
                      <div className="flex items-start gap-3 min-w-0">
                        <div className="rounded-lg bg-orange-100 p-2 text-orange-700">
                          <FaMotorcycle />
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-extrabold text-slate-900">{t("checkout.tiles.cod")}</div>
                          <div className="text-sm text-gray-500">{t("checkout.tiles.codSub")}</div>
                        </div>
                      </div>
                    </button>
                  </div>

                  {selectedPayment === "ONLINE" ? (
                    <div className="w-full rounded-xl border border-slate-200 bg-white p-4">
                      <div className="flex items-center gap-2 text-sm font-extrabold text-slate-900">
                        <FaLock className="text-slate-700" />
                        {t("checkout.card.enter")}
                      </div>
                      <div className="mt-4 grid gap-4">
                        <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-600">
                          {t("checkout.card.number")}
                          <input
                            value={cardNumber}
                            onChange={(e) => setCardNumber(e.target.value)}
                            className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm"
                            placeholder="1234 5678 9012 3456"
                            inputMode="numeric"
                          />
                        </label>
                        <div className="grid grid-cols-2 gap-4">
                          <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-600">
                            {t("checkout.card.expiry")}
                            <input
                              value={cardExpiry}
                              onChange={(e) => setCardExpiry(e.target.value)}
                              className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm"
                              placeholder="MM/YY"
                              inputMode="numeric"
                            />
                          </label>
                          <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-600">
                            CVV
                            <input
                              value={cardCvv}
                              onChange={(e) => setCardCvv(e.target.value)}
                              className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm"
                              placeholder="•••"
                              inputMode="numeric"
                            />
                          </label>
                        </div>
                        <div className="flex items-center gap-2 text-xs font-semibold text-emerald-700">
                          <FaLock />
                          {t("checkout.card.ssl")}
                        </div>
                      </div>
                    </div>
                  ) : null}

                  {selectedPayment === "JAZZCASH" ? (
                    <div className="relative w-full rounded-lg p-4 border border-gray-100 bg-gray-50 flex flex-col gap-y-4 shadow-sm z-0">
                      <div className="text-sm font-extrabold text-slate-900">{t("checkout.jazz.title")}</div>

                      <div className="w-full flex items-center justify-between gap-4 rounded-xl border border-dashed border-orange-300 bg-white p-4">
                        <div className="text-xs text-slate-600">
                          {t("checkout.jazz.sendTo")}
                          <div className="mt-2 text-xl font-extrabold tracking-[0.2em] text-orange-700" dir="ltr">{houseWallet.jazzcash_number}</div>
                        </div>
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(houseWallet.jazzcash_number || '');
                              toast?.success?.(t("subs.copied"));
                            } catch {
                              toast?.error?.(t("subs.copyFailed"));
                            }
                          }}
                          className="rounded-xl bg-orange-200/60 px-4 py-2 text-xs font-bold uppercase tracking-[0.12em] text-orange-900 hover:bg-orange-200"
                        >
                          <span className="inline-flex items-center gap-2">
                            <FaRegCopy />
                            {t("checkout.jazz.copy")}
                          </span>
                        </button>
                      </div>

                      <ol className="w-full space-y-3 text-sm text-slate-700">
                        {[
                          t("checkout.jazz.step1"),
                          `${t("checkout.jazz.step2Before")} ${houseWallet.jazzcash_number || ''} ${t("checkout.jazz.step2After")}`.trim(),
                          t("checkout.jazz.step3"),
                          t("checkout.jazz.step4"),
                          t("checkout.jazz.step5"),
                        ].map((step, idx) => (
                          <li key={step} className="flex gap-3">
                            <span className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-orange-200 text-xs font-extrabold text-orange-900">
                              {idx + 1}
                            </span>
                            <span className="flex-1">{step}</span>
                          </li>
                        ))}
                      </ol>

                      <label className="w-full cursor-pointer rounded-xl border border-dashed border-orange-300 bg-white p-5 text-center">
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => setPrescriptionFile(e.target.files?.[0] || null)}
                          className="hidden"
                        />
                        <div className="mx-auto flex flex-col items-center gap-2">
                          <FaCloudUploadAlt className="text-2xl text-orange-700" />
                          <div className="text-sm font-semibold text-slate-900">{t("checkout.jazz.upload")}</div>
                          <div className="text-xs text-slate-500">{t("checkout.jazz.supported")}</div>
                          {prescriptionFile ? (
                            <div className="mt-2 text-xs font-semibold text-emerald-700">{prescriptionFile.name}</div>
                          ) : null}
                        </div>
                      </label>
                    </div>
                  ) : null}

                  {selectedPayment === "COD" ? (
                    <div className="w-full rounded-xl border border-sky-200 bg-sky-50 p-4">
                      <div className="flex gap-3">
                        <FaInfoCircle className="mt-0.5 text-sky-700" />
                        <p className="text-sm text-sky-900">
                          {t("checkout.codNoticeBefore")} <span className="font-bold">{t("orderStatus.pending")}</span> {t("checkout.codNoticeAfter")}
                        </p>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}

            <div className="rounded-3xl border border-slate-200 bg-white p-5">
              <p className="text-sm font-semibold text-slate-900">{t("orderDetail.items")}</p>

              {/* Allow adding items for all modes (grocery/medicine/flower). */}
              <div className="mt-4">
                <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-600">
                  {t("subs.addProduct")}
                  <select
                    defaultValue=""
                    onChange={(e) => {
                      addItem(e.target.value);
                      e.target.value = "";
                    }}
                    className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm"
                  >
                    <option value="" disabled>
                      {t("subs.selectProduct")}
                    </option>
                    {(products || []).map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} — {formatPrice(p.price || 0)}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="mt-4 space-y-3">
                {items.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                    {mode === "medicine"
                      ? t("subs.empty.medicine")
                      : mode === "flower"
                        ? t("subs.empty.flower")
                        : t("subs.empty.grocery")}
                  </div>
                ) : (
                  items.map((it) => {
                    const p = productMap.get(it.product_id);
                    return (
                      <div key={it.product_id} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                        <img
                          src={getPrimaryProductImageSrc(p || {})}
                          alt=""
                          className="h-12 w-12 rounded-2xl border border-slate-200 bg-white object-contain p-1"
                          loading="lazy"
                          referrerPolicy="no-referrer"
                        />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-slate-900 truncate">{p?.name || t("orderDetail.productN", { id: it.product_id })}</p>
                          <p className="text-xs text-slate-500">{formatPrice(p?.price || 0)}</p>
                        </div>
                        <input
                          type="number"
                          min={1}
                          value={it.quantity}
                          onChange={(e) => updateQty(it.product_id, e.target.value)}
                          className="w-20 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900"
                        />
                        <button
                          onClick={() => removeItem(it.product_id)}
                          className="rounded-full border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-white"
                        >
                          {t("reviews.media.remove")}
                        </button>
                      </div>
                    );
                  })
                )}
              </div>

            </div>

            {/* ACTIONS: bottom right */}
            <div className="w-full flex flex-row justify-end items-center gap-4 pt-6 border-t border-gray-200 mt-4">
              <button
                onClick={onClose}
                className="rounded-full border border-slate-300 bg-white px-6 py-3 text-sm font-semibold uppercase tracking-[0.12em] text-slate-900 hover:bg-slate-50 w-full sm:w-auto"
              >
                {t("common.cancel")}
              </button>
              <button
                onClick={submit}
                disabled={submitting}
                className="rounded-full bg-slate-900 px-6 py-3 text-sm font-semibold uppercase tracking-[0.12em] text-white transition hover:bg-slate-800 disabled:bg-slate-300 w-full sm:w-auto"
              >
                {submitting ? t("subs.processing") : submitLabel}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

